import React, { HTMLAttributes, useState } from 'react'

import { css } from '@emotion/core'

import { useTheme } from 'emotion-theming'
import SVGIcon from 'components/common/atoms/SVGIcon'
import VisibleSVG from 'svgs/visible.svg'
import InvisibleSVG from 'svgs/invisible.svg'

interface PasswordInputProps<T> extends HTMLAttributes<T> {
  field?: {
    value: string | number
  }
  touched?: boolean
  error?: boolean
  disabled?: boolean
  value?: string | number | string[]
  onFocus?: () => void
  onChange?: () => void
}

const passwordInputStyle = (theme: ThemeType, hasValue: boolean) => css`
  position: relative;
  input {
    padding: 8px 16px;
    line-height: 1.5;
    font-weight: lighter;
    height: 48px;
    background-color: ${hasValue ? theme.neutral.white : theme.gray.gray10};
    color: ${hasValue ? theme.gray.gray60 : theme.gray.gray40};
    font-size: 14px;
    border: 1px solid ${hasValue ? theme.gray.gray30 : theme.gray.gray20};
    width: 100%;
    border-radius: 4px;
    transition: border 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:active,
    &:hover,
    &:focus-within {
      border-color: ${theme.gray.gray40};
      outline: none;
    }
    &:focus-within {
      box-shadow: 1px 1px 5px 2px ${theme.gray.gray30};
    }
    &:disabled {
      background-color: ${theme.gray.gray30};
      &:hover {
        border: 1px solid ${theme.gray.gray30};
      }
    }
    &::placeholder {
      color: ${theme.gray.gray40};
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 50px white inset;
      -webkit-text-fill-color: ${theme.gray.gray70};
      transition: background-color 5000s ease-in-out 0s;
    }
    &:-webkit-autofill::first-line {
      font-family: 'Open Sans', sans-serif;
      font-size: 14px;
      color: ${theme.gray.gray70};
      @media screen and (min-width: 768px) {
        font-size: 16px;
      }
    }
  }
  span {
    position: absolute;
    top: -3px;
    right: 8px;
    width: 24px;
    height: 24px;
  }
`

const passwordInputErrStyle = (theme: ThemeType) => css`
  border: solid 1px ${theme.error.red40} !important;
  background-color: ${theme.error.red10} !important;
  &:hover,
  &:active,
  &:focus-within {
    border: solid 1px ${theme.error.red40};
  }
  &::placeholder {
    color: ${theme.gray.gray40};
  }
  &:focus-within {
    box-shadow: 1px 1px 5px 2px ${theme.gray.gray30};
  }
`

const PasswordInput = (props: PasswordInputProps<{}>) => {
  const theme: ThemeType = useTheme()
  const { field, touched, error, disabled, value, onFocus, onChange, ...otherProps } = props
  const [isShowPassword, toggleShowPassword] = useState(false)
  const hasValue = Boolean(field?.value || value)
  return (
    <span css={passwordInputStyle(theme, hasValue)}>
      <input
        type={isShowPassword ? 'text' : 'password'}
        className="password-input"
        disabled={disabled || false}
        value={value}
        onFocus={onFocus}
        onChange={onChange}
        {...field}
        {...otherProps}
        css={touched && error && passwordInputErrStyle(theme)}
      />
      <span
        data-testid="password-input-icon"
        onClick={() => toggleShowPassword(!isShowPassword)}
        onKeyDown={() => toggleShowPassword(!isShowPassword)}
      >
        <SVGIcon name={isShowPassword ? 'visible' : 'invisible'} size="md" color={theme.gray.gray50}>
          {isShowPassword ? <VisibleSVG /> : <InvisibleSVG />}
        </SVGIcon>
      </span>
    </span>
  )
}

export default PasswordInput
